import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { 
  ArrowRight, 
  Shield, 
  ChartLine, 
  GraduationCap, 
  Buildings, 
  Certificate,
  Briefcase,
  Envelope,
  LinkedinLogo,
  Check,
  ArrowUpRight
} from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";

// Auth & Admin
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import AdminDashboard from "./pages/AdminDashboard";
import CustomerDetail from "./pages/CustomerDetail";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-[#555555]">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex items-center gap-3" data-testid="logo">
            <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">FR</span>
            </div>
            <span className="font-heading font-semibold text-lg text-[#111111] hidden sm:block">
              Fractional Risk Group
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            <a href="#vision" className="text-[#555555] hover:text-[#111111] transition-colors link-underline">About</a>
            <a href="#products" className="text-[#555555] hover:text-[#111111] transition-colors link-underline">Products</a>
            <a href="#founder" className="text-[#555555] hover:text-[#111111] transition-colors link-underline">Leadership</a>
            <a href="#contact" className="text-[#555555] hover:text-[#111111] transition-colors link-underline">Contact</a>
          </nav>

          <div className="flex items-center gap-4">
            <a href="#demo" className="btn-primary hidden sm:inline-flex items-center gap-2" data-testid="header-demo-btn">
              Request Demo
              <ArrowRight size={18} weight="bold" />
            </a>
            
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <div className="w-6 h-5 flex flex-col justify-between">
                <span className={`w-full h-0.5 bg-[#111111] transition-transform ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <span className={`w-full h-0.5 bg-[#111111] transition-opacity ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                <span className={`w-full h-0.5 bg-[#111111] transition-transform ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <nav className="md:hidden py-6 border-t border-[#E5E5E5]" data-testid="mobile-nav">
            <div className="flex flex-col gap-4">
              <a href="#vision" className="text-[#555555] hover:text-[#111111] py-2" onClick={() => setMobileMenuOpen(false)}>About</a>
              <a href="#products" className="text-[#555555] hover:text-[#111111] py-2" onClick={() => setMobileMenuOpen(false)}>Products</a>
              <a href="#founder" className="text-[#555555] hover:text-[#111111] py-2" onClick={() => setMobileMenuOpen(false)}>Leadership</a>
              <a href="#contact" className="text-[#555555] hover:text-[#111111] py-2" onClick={() => setMobileMenuOpen(false)}>Contact</a>
              <a href="#demo" className="btn-primary text-center mt-2" onClick={() => setMobileMenuOpen(false)}>Request Demo</a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const Hero = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/demo-request`, { email });
      toast.success("Demo request submitted! We'll be in touch soon.");
      setEmail("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <section className="hero-bg min-h-screen flex items-center pt-20" data-testid="hero-section" id="demo">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 relative z-10">
        <div className="max-w-4xl">
          <motion.p className="overline mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            Enterprise Risk Management
          </motion.p>
          
          <motion.h1 
            className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-[#111111] tracking-tight leading-[1.1] mb-8"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            data-testid="hero-title"
          >
            Enterprise-Grade AI<br /><span className="font-black">Risk Management</span>
          </motion.h1>
          
          <motion.p 
            className="text-lg lg:text-xl text-[#555555] max-w-2xl mb-12 leading-relaxed"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
            data-testid="hero-subtitle"
          >
            For organizations that demand sophistication without the overhead of a full risk department. 
            Built by a former Chief Risk Officer for the realities of mid-market enterprise.
          </motion.p>

          <motion.form 
            onSubmit={handleDemoRequest}
            className="flex flex-col sm:flex-row gap-4 max-w-xl"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your work email"
              className="input-sharp flex-1"
              data-testid="hero-email-input"
              required
            />
            <button 
              type="submit" 
              className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap"
              disabled={isSubmitting}
              data-testid="hero-demo-submit"
            >
              {isSubmitting ? "Submitting..." : "Request Demo"}
              <ArrowRight size={18} weight="bold" />
            </button>
          </motion.form>

          <motion.div 
            className="flex flex-wrap gap-6 mt-12"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="flex items-center gap-2 text-sm text-[#555555]">
              <Check size={18} weight="bold" className="text-[#002FA7]" />
              No credit card required
            </div>
            <div className="flex items-center gap-2 text-sm text-[#555555]">
              <Check size={18} weight="bold" className="text-[#002FA7]" />
              Personalized demo
            </div>
            <div className="flex items-center gap-2 text-sm text-[#555555]">
              <Check size={18} weight="bold" className="text-[#002FA7]" />
              Built by a former CRO
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Vision Section
const Vision = () => (
  <section className="py-24 lg:py-32 bg-white" id="vision" data-testid="vision-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <p className="overline mb-6">Our Vision</p>
          <h2 className="font-heading font-light text-3xl lg:text-4xl text-[#111111] tracking-tight mb-8">
            Sophistication <span className="font-black">Without the Overhead</span>
          </h2>
          <div className="space-y-6 text-[#555555] leading-relaxed">
            <p>Mid-market organizations face a paradox: they have risk profiles as complex as Fortune 500 companies, but can't justify the overhead of a full Chief Risk Officer team.</p>
            <p>Fractional Risk Group bridges this gap with AI-powered tools built by practitioners who've lived in your world. We speak your language — total cost of risk, captive feasibility, risk culture assessment, bow-tie analysis, emerging risk radar.</p>
            <p>Our suite of products delivers enterprise-grade risk management capabilities at a fraction of the traditional cost, allowing you to make better decisions and defend them to your board with confidence.</p>
          </div>
          <a href="#products" className="btn-secondary inline-flex items-center gap-2 mt-8" data-testid="vision-cta">
            Explore Our Products
            <ArrowRight size={18} weight="bold" />
          </a>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1624213011686-bc14dfc81650?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbGFzcyUyMGNvcnBvcmF0ZSUyMG9mZmljZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzU0MzU3OTJ8MA&ixlib=rb-4.1.0&q=85"
              alt="Modern corporate architecture"
              className="w-full h-[400px] lg:h-[500px] object-cover"
              data-testid="vision-image"
            />
            <div className="absolute -bottom-6 -left-6 bg-white p-8 border border-[#E5E5E5] max-w-xs hidden lg:block">
              <p className="font-heading font-black text-4xl text-[#002FA7] mb-2">15+</p>
              <p className="text-sm text-[#555555]">Years of enterprise risk management experience</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Product Card
const ProductCard = ({ icon: Icon, title, description, audience, url, outcomes }) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/demo-request`, { email, product_interest: title });
      toast.success(`Demo request for ${title} submitted!`);
      setEmail("");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <div className="product-card bg-white border border-[#E5E5E5] p-8 lg:p-10" data-testid={`product-card-${title.toLowerCase().replace(/\./g, '-')}`}>
      <div className="w-14 h-14 bg-[#F3F3F3] flex items-center justify-center mb-6">
        <Icon size={28} weight="regular" className="text-[#002FA7]" />
      </div>
      
      <h3 className="font-heading font-bold text-xl lg:text-2xl text-[#111111] mb-2">{title}</h3>
      <p className="text-sm text-[#002FA7] mb-4">{audience}</p>
      <p className="text-[#555555] mb-6 leading-relaxed">{description}</p>
      
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wider text-[#555555] mb-3">Key Outcomes</p>
        <ul className="space-y-2">
          {outcomes.map((outcome, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-[#555555]">
              <Check size={16} weight="bold" className="text-[#002FA7] mt-0.5 flex-shrink-0" />
              {outcome}
            </li>
          ))}
        </ul>
      </div>

      <form onSubmit={handleDemoRequest} className="space-y-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Work email"
          className="input-sharp w-full text-sm"
          required
        />
        <button type="submit" className="btn-primary w-full text-sm flex items-center justify-center gap-2" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Request Demo"}
          <ArrowRight size={16} weight="bold" />
        </button>
      </form>
      
      <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 text-sm text-[#002FA7] mt-4 hover:underline">
        Visit {title} <ArrowUpRight size={14} />
      </a>
    </div>
  );
};

// Products Section
const Products = () => {
  const products = [
    {
      icon: ChartLine,
      title: "fractionalrisk.ai",
      audience: "For CROs, Risk Managers & CFOs",
      description: "AI-powered risk optimization platform that transforms how you manage total cost of risk, evaluate captive feasibility, and make data-driven retention decisions.",
      url: "https://fractionalrisk.ai",
      outcomes: ["Lower total cost of risk", "Data-driven retention decisions", "Board-ready risk analytics"]
    },
    {
      icon: Shield,
      title: "risksurvey.ai",
      audience: "For Carriers, MGAs & Program Managers",
      description: "Revolutionize loss control surveys with AI-powered speed and accuracy. Reduce survey costs while improving data quality and risk selection.",
      url: "https://risksurvey.ai",
      outcomes: ["50% faster survey completion", "Improved risk selection accuracy", "Reduced operational costs"]
    },
    {
      icon: GraduationCap,
      title: "riskcoach.ai",
      audience: "For Risk Managers & HR/L&D Teams",
      description: "Executive training platform focused on presence and resilience. AI coaching combined with cohort-based learning for lasting professional development.",
      url: "https://riskcoach.ai",
      outcomes: ["Executive presence development", "Communication and Influence", "Resilience and Crisis Leadership"]
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#FAFAFA]" id="products" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-16">
          <p className="overline mb-6">Product Suite</p>
          <h2 className="font-heading font-light text-3xl lg:text-4xl text-[#111111] tracking-tight mb-6">
            Three Platforms. <span className="font-black">One Mission.</span>
          </h2>
          <p className="text-[#555555] leading-relaxed">
            Each product addresses a distinct challenge in the risk management ecosystem, unified by our commitment to delivering enterprise-grade capabilities without enterprise overhead.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, idx) => (
            <ProductCard key={idx} {...product} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Founder Section
const Founder = () => (
  <section className="py-24 lg:py-32 bg-white" id="founder" data-testid="founder-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <div className="w-full h-[500px] lg:h-[600px] bg-[#002FA7] flex items-center justify-center">
            <div className="text-center text-white">
              <div className="w-32 h-32 mx-auto mb-6 border-4 border-white/30 rounded-full flex items-center justify-center">
                <Briefcase size={48} weight="light" />
              </div>
              <p className="text-xl font-heading font-light">Founded by a</p>
              <p className="text-2xl font-heading font-bold">Former CRO</p>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7">
          <p className="overline mb-6">Leadership</p>
          <h2 className="font-heading font-light text-3xl lg:text-4xl text-[#111111] tracking-tight mb-8">
            Built by Someone Who's <span className="font-black">Been in Your Seat</span>
          </h2>
          
          <div className="flex flex-wrap gap-3 mb-8">
            <div className="credential-badge" data-testid="credential-cro">
              <Briefcase size={18} />
              Former Chief Risk Officer
            </div>
            <div className="credential-badge" data-testid="credential-doctorate">
              <Certificate size={18} />
              Doctorate in Organizational Leadership
            </div>
            <div className="credential-badge" data-testid="credential-broker">
              <Buildings size={18} />
              Licensed Insurance Broker
            </div>
          </div>

          <div className="space-y-6 text-[#555555] leading-relaxed">
            <p>These aren't tools built by technologists guessing at what risk professionals need. They're built by someone who's sat across the table from underwriters, defended risk strategies to skeptical boards, and navigated the complex realities of enterprise risk management.</p>
            <p>That experience infuses every feature, every workflow, every output. When the platform suggests a retention strategy or generates a risk report, it speaks the language you speak — because we've spoken it in the boardrooms where these decisions actually get made.</p>
            <p className="font-medium text-[#111111]">"The best technology disappears into the background. What remains is better decisions, made faster, with more confidence."</p>
          </div>

          <a href="#demo" className="btn-primary inline-flex items-center gap-2 mt-8" data-testid="founder-demo-btn">
            Schedule a Conversation
            <ArrowRight size={18} weight="bold" />
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Partners Marquee
const PartnersMarquee = () => {
  const items = ["Total Cost of Risk", "Captive Feasibility", "Risk Culture Assessment", "Bow-Tie Analysis", "Emerging Risk Radar", "Loss Control", "ERM Best Practices", "Monte Carlo Simulation", "Risk Retention", "Program Design"];
  return (
    <section className="py-12 border-y border-[#E5E5E5] bg-[#F3F3F3]" data-testid="marquee-section">
      <Marquee speed={40} gradient={false}>
        {items.map((item, idx) => (
          <span key={idx} className="marquee-item mx-12">{item}</span>
        ))}
      </Marquee>
    </section>
  );
};

// Footer
const Footer = () => {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", subject: "General Inquiry", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", company: "", subject: "General Inquiry", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <footer className="footer-dark" id="contact" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <h2 className="font-heading font-light text-4xl lg:text-5xl tracking-tight mb-8">
              Let's <span className="font-black">Talk</span>
            </h2>
            <p className="text-white/70 leading-relaxed mb-8 max-w-md">
              Whether you're ready to schedule a demo, explore a pilot program, or simply have questions about how our platforms can serve your organization.
            </p>
            
            <div className="space-y-4 mb-12">
              <a href="mailto:contact@fractionalriskgroup.com" className="flex items-center gap-3 text-white/70 hover:text-white">
                <Envelope size={20} />
                contact@fractionalriskgroup.com
              </a>
              <a href="https://www.linkedin.com/in/billraab/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/70 hover:text-white">
                <LinkedinLogo size={20} />
                Connect on LinkedIn
              </a>
            </div>

            <div className="flex gap-6">
              <a href="https://fractionalrisk.ai" target="_blank" rel="noopener noreferrer" className="text-sm text-white/50 hover:text-white">fractionalrisk.ai</a>
              <a href="https://risksurvey.ai" target="_blank" rel="noopener noreferrer" className="text-sm text-white/50 hover:text-white">risksurvey.ai</a>
              <a href="https://riskcoach.ai" target="_blank" rel="noopener noreferrer" className="text-sm text-white/50 hover:text-white">riskcoach.ai</a>
            </div>
          </div>

          <div>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Name" className="bg-white/10 border border-white/20 text-white placeholder:text-white/50 px-5 py-4 focus:border-white focus:outline-none" data-testid="contact-name" required />
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Work Email" className="bg-white/10 border border-white/20 text-white placeholder:text-white/50 px-5 py-4 focus:border-white focus:outline-none" data-testid="contact-email" required />
              </div>
              <input type="text" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder="Company" className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 px-5 py-4 focus:border-white focus:outline-none" data-testid="contact-company" />
              <select value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="w-full bg-white/10 border border-white/20 text-white px-5 py-4 focus:border-white focus:outline-none appearance-none" data-testid="contact-subject">
                <option value="General Inquiry" className="bg-[#111111]">General Inquiry</option>
                <option value="Demo Request" className="bg-[#111111]">Demo Request</option>
                <option value="Partnership" className="bg-[#111111]">Partnership Opportunity</option>
                <option value="Careers" className="bg-[#111111]">Careers</option>
              </select>
              <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Your message" rows={4} className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 px-5 py-4 focus:border-white focus:outline-none resize-none" data-testid="contact-message" required />
              <button type="submit" className="bg-white text-[#111111] px-8 py-4 font-medium hover:bg-white/90 transition-colors flex items-center gap-2" disabled={isSubmitting} data-testid="contact-submit">
                {isSubmitting ? "Sending..." : "Send Message"}
                <ArrowRight size={18} weight="bold" />
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/50">© {new Date().getFullYear()} Fractional Risk Group. All rights reserved.</p>
            <div className="flex gap-6 text-sm text-white/50">
              <a href="/privacy" className="hover:text-white">Privacy Policy</a>
              <a href="/terms" className="hover:text-white">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Home Page
const Home = () => (
  <div className="min-h-screen" data-testid="home-page">
    <Header />
    <main>
      <Hero />
      <Vision />
      <Products />
      <Founder />
      <PartnersMarquee />
    </main>
    <Footer />
    <Toaster position="bottom-right" richColors />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/customers/:id" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
